// What the Explorer's two big listings send to the server, now that both are
// keyset-paged (200 a page) rather than fetched whole.
//
// The maps and the translation below live here rather than in the page because
// they are the wire contract's browser half: each one pairs a control the reader
// touches with the parameter the route takes, and getting either wrong is silent
// — the list still renders, it is just answering a different question.
import { NO_VALUE } from "@recruiter/shared";

/** Sort keys the server can order the whole PEOPLE list by, keyed by the
 *  table's own sort key (`repo.users.USER_SORTS` is the other half).
 *
 *  The project counts are absent because they are per-row subqueries: ordering
 *  the table by one would compute it for every user before the page could be
 *  cut, which is the cost the paging exists to remove. */
export const PERSON_SERVER_SORTS: Readonly<Record<string, string>> = {
  person: "person",
  lastActive: "active",
  joined: "joined",
};

/** Sort keys the server can order the whole PROJECTS list by
 *  (`repo.projects.PROJECT_SORTS`).
 *
 *  Stage, budget, timeline, alerts and message count are derived per row by the
 *  listing's enrichment, so the same argument applies. The project TITLE is
 *  absent for a different reason: it is derived in the BROWSER
 *  (`lib/project-title.ts` falls back through summary to a short id, with a
 *  placeholder list this bundle owns), so a database ordering by `projects.name`
 *  would be ordering by text the reader never sees — which reads as a broken
 *  sort rather than a different one. */
export const PROJECT_SERVER_SORTS: Readonly<Record<string, string>> = {
  kind: "kind",
  workShape: "workShape",
  owner: "owner",
  updated: "updated",
};

/**
 * A two-option filter selection as the boolean the route takes.
 *
 * Both ticked is the SAME question as neither ticked — "either" — and must send
 * no parameter at all rather than one of them. Sending one would mean ticking
 * every box in a filter narrowed the list, which is the opposite of what a
 * reader ticking every box is asking for.
 */
export function selectionAsBoolean(
  selection: readonly string[] | undefined,
  whenTrue: string,
  whenFalse: string,
): boolean | undefined {
  const yes = selection?.includes(whenTrue) ?? false;
  const no = selection?.includes(whenFalse) ?? false;
  if (yes === no) return undefined;
  return yes;
}

/**
 * The alert-reason selection as the route's vocabulary.
 *
 * The shared filter marks "this row has no value for the column" with its own
 * sentinel; the route spells that bucket `none`. Translating here keeps a
 * frontend constant out of the wire contract — and keeps "no open alerts", which
 * is a bucket operators actively look for, from being silently dropped.
 */
export function alertSelectionToWire(selection: readonly string[]): string[] {
  return selection.map((value) => (value === NO_VALUE ? "none" : value));
}

/**
 * A selection as a route's own spelling of the NO-VALUE bucket.
 *
 * The shared filter marks "this row has no value for the column" with its own
 * sentinel; the device, model-tier and country routes all spell that bucket
 * `unknown`. Sending the sentinel itself is a 422 (each route's closed vocabulary
 * refuses it), which is exactly what the Explorer's Device and Model filters did
 * before this existed: ticking Unknown broke the list instead of narrowing it.
 */
export function noValueAs(selection: readonly string[], bucket: string): string[] {
  return selection.map((value) => (value === NO_VALUE ? bucket : value));
}
